import { useCallback, useState } from 'react';

import { AutoComplete, Input } from 'antd';
import { Search, MapPin, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface OpenMeteoGeocodingItem {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  admin1?: string;
  country?: string;
}

interface OpenMeteoGeocodingResponse {
  results?: OpenMeteoGeocodingItem[];
}

interface WeatherLocationSearchProps {
  onSelect: (lat: number, lng: number, name: string) => void;
  style?: React.CSSProperties;
}

/**
 * Component tìm kiếm địa điểm sử dụng Vietmap Autocomplete & Place Detail API (v4).
 */
export function WeatherLocationSearch({ onSelect, style }: WeatherLocationSearchProps) {
  const { t } = useTranslation();
  const [options, setOptions] = useState<
    { label: React.ReactNode; value: string; lat: number; lng: number }[]
  >([]);
  const [fetching, setFetching] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const handleSearch = useCallback(async (text: string) => {
    if (!text || text.length < 2) {
      setOptions([]);
      return;
    }

    setFetching(true);
    try {
      const res = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(text)}&count=10&language=vi&format=json`,
      );
      const data: OpenMeteoGeocodingResponse = await res.json();

      if (data.results) {
        setOptions(
          data.results.map((item) => {
            const display = [item.name, item.admin1, item.country].filter(Boolean).join(', ');
            return {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
                  <MapPin size={14} className="text-blue-600 shrink-0 dark:text-blue-400" />
                  <div
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{item.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{display}</div>
                  </div>
                </div>
              ),
              value: display,
              lat: item.latitude,
              lng: item.longitude,
            };
          }),
        );
      }
    } catch (err) {
      console.error('[Open-Meteo Search] Geocoding error:', err);
    } finally {
      setFetching(false);
    }
  }, []);

  const handleSelect = useCallback(
    (_value: string, option: { lat: number; lng: number; value: string }) => {
      onSelect(option.lat, option.lng, option.value);
      setSearchValue('');
      setOptions([]);
    },
    [onSelect],
  );

  return (
    <div style={{ width: '100%', ...style }}>
      <AutoComplete
        options={options}
        onSearch={handleSearch}
        onSelect={handleSelect}
        value={searchValue}
        onChange={setSearchValue}
        className="w-full"
        popupClassName="weather-search-dropdown"
      >
        <Input
          prefix={
            fetching ? (
              <Loader2 size={16} className="animate-spin text-blue-500" />
            ) : (
              <Search size={16} className="text-slate-400" />
            )
          }
          placeholder={t('weather.searchLocationPlaceholder') || 'Tìm địa điểm...'}
          allowClear
          className="!h-10 !rounded-xl !border-[var(--glass-border)] !bg-[var(--bg-card)] !text-sm dark:!text-slate-200"
          style={{
            boxShadow: 'var(--shadow-sm)',
          }}
        />
      </AutoComplete>
    </div>
  );
}
